import { useState } from 'react';
import { useAsyncEffect } from '@hilma/tools'
import { useAlert, FormAutocomplete, FormInput, useForm } from '@hilma/forms';

//mui
import LocationOnIcon from '@material-ui/icons/LocationOn';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import parse from 'autosuggest-highlight/parse';
import { AutocompletePrediction, AutocompleteService } from '../types';

const autocompleteService: { current: AutocompleteService | null } = { current: null };

interface LocationAutocompleteProps {
	id: string;
	description: string;
	lat?: number;
	lng?: number;
	noOptionsText?: string;
	address?: string;
	loading?: boolean;
	disabled?: boolean;
	classes?: any;
}

const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({ id, description, address, lat, lng, noOptionsText, loading, disabled = false, classes: externalClasses }) => {
	const [initialLocation, setInitialLocation] = useState<AutocompletePrediction | undefined>();

	const innerClasses = useStyles();
	const alert = useAlert();
	const form = useForm();

	useAsyncEffect(async () => {
		if (loading || !lat || !lng || !description || !address) return;
		try {
			const predictions = await fetchPredictions(address, new google.maps.LatLng(lat, lng));
			setInitialLocation(predictions[0]);
		} catch (err) {
			console.error('err: ', err);
			alert('שגיאה בהבאת מידע')
		}
	}, [lat, lng, loading, description]);

	const fetchPredictions = async (input: string, location?: google.maps.LatLng) => {
		if (!input) return [];
		
		// Check if Google Maps API is loaded
		if (!window.google || !window.google.maps || !window.google.maps.places) {
			console.error('Google Maps API not loaded');
			return [];
		}
		
		// Initialize the AutocompleteService if not already initialized
		if (!autocompleteService.current) {
			autocompleteService.current = new window.google.maps.places.AutocompleteService();
		}
		
		try {
			// Use the AutocompleteService to get predictions
			const predictions = await new Promise<AutocompletePrediction[]>((resolve, reject) => {
				autocompleteService.current!.getPlacePredictions(
					{ input, ...(location ? { location, radius: 1 } : {}) },
					(results, status) => {
						if (status === google.maps.places.PlacesServiceStatus.OK && results) {
							resolve(results);
						} else {
							console.error('Places API error:', status);
							resolve([]);
						}
					}
				);
			});
			return predictions || [];
		} catch (error) {
			console.error('Error fetching predictions:', error);
			return [];
		}
	}

	// Always render as an interactive FormInput when loading or waiting for location data
	if (loading || (lat && lng && !initialLocation)) return <FormInput id={id} description={description} loading={true} />

	// If Google Maps API is not available or not fully loaded, render a simple FormInput
	if (!window.google || !window.google.maps || !window.google.maps.places) {
		return <FormInput id={id} description={description} defaultValue={form.values[id]} />
	}

	return (
		<FormAutocomplete
			disabled={disabled}
			id={id}
			classes={externalClasses}
			description={description}
			noOptionsText={noOptionsText || "הכנס כתובת"}
			initialValue={initialLocation}
			fetchFunction={fetchPredictions}
			getOptionSelected={(option: AutocompletePrediction, value: AutocompletePrediction) => value.place_id === option.place_id}
			defaultValue={form.values[id]?.description ?? form.values[id]}
			getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
			onChange={(event, value) => {
				console.log('Location selected:', value);
				
				// If a valid location is selected, get its details
				if (value && value.place_id) {
					try {
						// First update the address field with the selected value's description
						const selectedAddress = value.description || "";
						
						const geocoder = new google.maps.Geocoder();
						geocoder.geocode({ placeId: value.place_id }, (results, status) => {
							console.log('Geocoder results:', results, 'status:', status);
							
							if (status === "OK" && results && results[0]) {
								const location = results[0].geometry.location;
								const lat = location.lat();
								const lng = location.lng();
								
								console.log('Location coordinates:', lat, lng, 'place_id:', value.place_id);
								
								// Store all values at once: address, lat, lng, and place_id
								form.setValues({
									...form.values,
									[id]: selectedAddress,
									lat: lat,
									lng: lng,
									cityId: value.place_id
								});
								
								console.log('Updated form values:', form.values);
							} else {
								console.error('Geocoder failed:', status);
								// Still update the address even if geocoding fails
								form.setValues({
									...form.values,
									[id]: selectedAddress
								});
							}
						});
					} catch (error) {
						console.error('Error in geocoding:', error);
						// Still update the address even if there's an error
						form.setValues({
							...form.values,
							[id]: value.description || ""
						});
					}
				} else if (value === null) {
					// If selection is cleared, clear all location data
					form.setValues({
						...form.values,
						[id]: "",
						lat: null,
						lng: null,
						cityId: ""
					});
				}
				// Don't update the form value if the user is just typing
				// The value will be updated only when a selection is made
			}}
			renderOption={(option) => {
				const matches = option?.structured_formatting?.main_text_matched_substrings || [];
				const parts = parse(
					option.structured_formatting.main_text,
					matches.map(match => [match.offset, match.offset + match.length]),
				);
				return (
					<Grid container alignItems="center" >
						<Grid item>
							<LocationOnIcon className={innerClasses.icon} />
						</Grid>
						<Grid item xs>
							{parts.map((part: { text: string; highlight: boolean }, index: number) => (
								<span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
									{part.text}
								</span>
							))}
							<Typography variant="body2" color="textSecondary">
								{option.structured_formatting.secondary_text}
							</Typography>
						</Grid>
					</Grid>
				);
			}}
		/>
	);
}

export default LocationAutocomplete

const useStyles = makeStyles(theme => ({
	icon: {
		color: theme.palette.text.secondary,
		marginRight: theme.spacing(2),
	},

}));
